import React, {useMemo} from 'react'
import {
    Flex,
    Box,
    Text,
    useMultiStyleConfig,
    Divider,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import RecipeUtensils from './recipe-utensils'
import {useGetRecipeDetails} from '../utils/use-get-recipe-info'
import {FormattedMessage} from 'react-intl'

const IngredientsMenu = ({ingredients, servings, allUtensils = {}}) => {
    const styles = useMultiStyleConfig('RecipeDetails', {})
    const {ingredientsMenu} = styles || {}

    const CONTENT_TYPE_INGREDIENT = 'ingredient'
    const CONTENT_TYPE_UNIT = 'unit'

    // Split into grouped vs main ingredients
    const {groupedIngredients, mainIngredients} = useMemo(() => {
        if (!ingredients?.length) return {groupedIngredients: [], mainIngredients: []}

        const grouped = []
        const main = []

        for (let ing of ingredients) {
            if (ing?.ingredient_group) {
                grouped.push(ing)
            } else {
                main.push(ing)
            }
        }

        return {groupedIngredients: grouped, mainIngredients: main}
    }, [ingredients])

    // Collect ingredient IDs
    const ingredientIds = useMemo(() => {
        if (!ingredients?.length) return []

        return [
            ...new Set(
                ingredients.flatMap((ing) =>
                    ing?.ingredient?.map((i) => i.mainIngredient?.ingredientId),
                ),
            ),
        ].filter(Boolean)
    }, [ingredients])

    const {data: allIngredientsData = []} = useGetRecipeDetails(
        ingredientIds,
        CONTENT_TYPE_INGREDIENT,
    )

    // Collect unit IDs
    const unitIds = useMemo(() => {
        if (!ingredients?.length) return []

        return [
            ...new Set(
                ingredients.flatMap((ing) => ing?.ingredient?.map((i) => i.mainIngredient?.unitId)),
            ),
        ].filter(Boolean)
    }, [ingredients])

    const {data: allUnitsData = []} = useGetRecipeDetails(unitIds, CONTENT_TYPE_UNIT)

    return (
        <Box {...ingredientsMenu?.outerContainer}>
            <Box {...ingredientsMenu?.stickyContainer}>
                <Text {...ingredientsMenu?.ingredientsTitle}>
                    <FormattedMessage
                        id="recipes.details_page.ingredients.title"
                        defaultMessage="Ingredients"
                    />
                </Text>

                <Flex>
                    {servings?.servings > 0 && (
                        <Flex {...ingredientsMenu?.servingsContainer}>
                            <Text {...ingredientsMenu?.ingredientMenuTitle}>
                                {' '}
                                <FormattedMessage
                                    id="recipes.details_page.servings.title"
                                    defaultMessage="Servings"
                                />
                            </Text>
                            <Text {...ingredientsMenu?.servingsNumber}>
                                {servings.servings.toString()}
                            </Text>
                        </Flex>
                    )}
                    {servings?.makes > 0 && (
                        <Flex {...ingredientsMenu?.servingsContainer}>
                            <Text {...ingredientsMenu?.ingredientMenuTitle}>
                                {' '}
                                <FormattedMessage
                                    id="recipe.details_page.servings.makes_text"
                                    defaultMessage="Makes"
                                />
                            </Text>
                            <Text {...ingredientsMenu?.servingsNumber}>
                                {servings.makes.toString()}
                            </Text>
                        </Flex>
                    )}
                </Flex>

                {/* Grouped Ingredients */}
                {groupedIngredients.map((ing, idx) => (
                    <Flex
                        data-testid={`grouped-ingredient=${idx}`}
                        key={`group-${idx}`}
                        {...ingredientsMenu?.ingredientContainer}
                    >
                        <Text {...ingredientsMenu?.ingredientMenuTitle}>
                            {ing?.ingredient_group}
                        </Text>
                        {ing?.ingredient?.map((mainIng, subIdx) => {
                            const ingredient = allIngredientsData.find(
                                (obj) => obj.uid === mainIng?.mainIngredient?.ingredientId,
                            )
                            const unit = allUnitsData.find(
                                (u) => u.uid === mainIng?.mainIngredient?.unitId,
                            )
                            return (
                                <Text
                                    key={`group-${ing?._metadata?.uid || idx}-${subIdx}`}
                                    {...ingredientsMenu?.ingredientText}
                                >
                                    {`${mainIng?.mainIngredient?.quantity || ''} ${
                                        unit?.title || ''
                                    } ${ingredient?.title || ''}${
                                        mainIng?.mainIngredient?.notes
                                            ? `, ${mainIng.mainIngredient.notes}`
                                            : ''
                                    }`}
                                </Text>
                            )
                        })}
                    </Flex>
                ))}

                {/* Main Ingredients */}
                {mainIngredients.map((ing, idx) => (
                    <Flex
                        data-testid={`main-ingredient=${idx}`}
                        key={`main-${idx}`}
                        {...ingredientsMenu?.ingredientContainer}
                    >
                        {ing?.ingredient?.map((mainIng, subIdx) => {
                            const ingredient = allIngredientsData.find(
                                (obj) => obj.uid === mainIng?.mainIngredient?.ingredientId,
                            )
                            const unit = allUnitsData.find(
                                (u) => u.uid === mainIng?.mainIngredient?.unitId,
                            )
                            return (
                                <Text
                                    key={`main-${ing?._metadata?.uid || idx}-${subIdx}`}
                                    {...ingredientsMenu?.ingredientText}
                                >
                                    {`${mainIng?.mainIngredient?.quantity || ''} ${
                                        unit?.title || ''
                                    } ${ingredient?.title || ''}${
                                        mainIng?.mainIngredient?.notes
                                            ? `, ${mainIng.mainIngredient.notes}`
                                            : ''
                                    }`}
                                </Text>
                            )
                        })}
                    </Flex>
                ))}

                <Divider />

                {Object.keys(allUtensils).length > 0 && (
                    <RecipeUtensils allUtensils={allUtensils} />
                )}
            </Box>
        </Box>
    )
}

IngredientsMenu.propTypes = {
    ingredients: PropTypes.array,
    servings: PropTypes.object,
    allUtensils: PropTypes.array,
}

export default IngredientsMenu
